import React, { useRef, useState } from "react"

import {
    COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG,
    OneVerifyEraUploadFileFormat,
} from "@common-shared/config/oneVerify/oneVerifyIntegrations.config"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"

import { INTEGRATIONS_CONFIG, EraUploadBulkUploadStage } from "../../../config/integrations.config"
import { EraUploadHelper } from "../../../helpers/eraUpload.helper"
import type { IEraUploadReviewState } from "../../../types/integrations.types"

export const EraUploadFileDropzoneComponent: React.FunctionComponent<
    IEraUploadFileDropzoneComponentProps
> = props => {
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [stage, setStage] = useState<EraUploadBulkUploadStage>(
        EraUploadBulkUploadStage.INITIAL,
    )

    const [errorMessage, setErrorMessage] = useState<string | null>(null)

    const x12ExtensionsLabel =
        COMMON_ONE_VERIFY_INTEGRATIONS_CONFIG.eraUpload.formatX12ExtensionsLabel()

    const isCsvFormat =
        props.acceptedFileFormat === OneVerifyEraUploadFileFormat.csv

    const resetState = () => {
        setErrorMessage(null)

        setStage(EraUploadBulkUploadStage.INITIAL)
    }

    const handleFile = async (file: File) => {
        if (props.isDisabled) {
            return
        }

        setErrorMessage(null)

        setStage(EraUploadBulkUploadStage.VALIDATING)

        const fileFormat = await EraUploadHelper.detectFileFormat(file, {
            isCsvUploadEnabled: isCsvFormat,
        })

        if (!fileFormat || fileFormat !== props.acceptedFileFormat) {
            setErrorMessage(
                isCsvFormat
                    ? "Unsupported file type. Upload a .csv spreadsheet."
                    : `Unsupported file type. Upload an X12 835 file (${x12ExtensionsLabel}).`,
            )

            setStage(EraUploadBulkUploadStage.ERROR)

            return
        }

        if (fileFormat === OneVerifyEraUploadFileFormat.x12) {
            const validation = await EraUploadHelper.validateX12File(file)

            if (validation.isValid) {
                resetState()

                props.onValidationDone({
                    fileFormat,
                    rows: [],
                    filename: file.name,
                    file,
                    totalRowCount: 0,
                })

                return
            }

            setErrorMessage(validation.errorMessage)

            setStage(EraUploadBulkUploadStage.ERROR)

            return
        }

        const preview = await EraUploadHelper.parseCsvFilePreview(file)

        if (preview.validation.isValid && preview.totalRowCount) {
            resetState()

            props.onValidationDone({
                fileFormat,
                rows: preview.rows,
                filename: file.name,
                file,
                totalRowCount: preview.totalRowCount,
            })

            return
        }

        setErrorMessage(
            preview.validation.errorMessage ??
                "This file could not be validated. Check the template and try again.",
        )

        setStage(EraUploadBulkUploadStage.ERROR)
    }

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault()

        e.stopPropagation()
    }

    const onDrop = (e: React.DragEvent) => {
        onDragOver(e)

        if (props.isDisabled) {
            return
        }

        const file = e.dataTransfer?.files?.[0]

        if (file) {
            void handleFile(file)
        }
    }

    const onManualSelect = (e: React.MouseEvent) => {
        e.stopPropagation()

        if (!props.isDisabled) {
            fileInputRef.current?.click()
        }
    }

    const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]

        if (file) {
            void handleFile(file)
        }

        e.target.value = ""
    }

    const isValidating = stage === EraUploadBulkUploadStage.VALIDATING

    if (stage === EraUploadBulkUploadStage.ERROR) {
        return (
            <div className={props.className}>
                <PrimaryText
                    typography="subtitleSemibold"
                    className="
                        rounded-[8px] border border-solid border-brand-black-transparent-010
                        bg-brand-offWhite py-[16px] px-[24px]
                    "
                    color={NIRVANA_COLORS.brand.error}
                >
                    {errorMessage}
                </PrimaryText>

                <div className="flex justify-end mt-[16px]">
                    <ButtonElement
                        label="Try again"
                        type="primary"
                        htmlType="button"
                        onClick={resetState}
                    />
                </div>
            </div>
        )
    }

    if (isValidating) {
        return (
            <div
                className={`
                    py-[16px] px-[24px] rounded-[8px] bg-brand-offWhite flex flex-col gap-[8px]
                    border-2 border-dashed border-brand-warmShadow
                    min-h-[200px] justify-center
                    ${props.className ?? ""}
                `}
            >
                <PrimaryText typography="subtitleSemibold">
                    Validating your ERA file...
                </PrimaryText>

                <PrimaryText color={NIRVANA_COLORS.brand.grey7}>
                    Checking file structure before upload. Large files may take
                    a moment.
                </PrimaryText>

                <div className="flex flex-col gap-[12px] mt-[8px]">
                    {[72, 88, 64, 96, 80].map((w, i) => (
                        <div
                            key={i}
                            className="h-[16px] rounded-[4px] bg-brand-warmLight animate-pulse"
                            style={{ width: `${w}%` }}
                        />
                    ))}
                </div>
            </div>
        )
    }

    return (
        <div className={props.className}>
            <input
                ref={fileInputRef}
                type="file"
                accept={INTEGRATIONS_CONFIG.eraUpload.getFileAcceptForFormat(
                    props.acceptedFileFormat,
                )}
                className="hidden"
                onChange={onFileInputChange}
                disabled={props.isDisabled}
            />

            <div
                className={`
                    flex flex-col items-center justify-center
                    min-h-[200px] p-[32px] rounded-[8px] gap-[12px]
                    border-2 border-dashed border-brand-warmShadow bg-brand-offWhite
                    ${props.isDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
                `}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onClick={onManualSelect}
            >
                <PrimaryText typography="subtitleSemibold">
                    Drag & drop your file here
                </PrimaryText>

                <PrimaryText color={NIRVANA_COLORS.brand.grey7} centered>
                    {isCsvFormat
                        ? "Supports .csv files"
                        : `Supports X12 835 files (${x12ExtensionsLabel})`}
                </PrimaryText>

                <ButtonElement
                    type="primary"
                    htmlType="button"
                    label="Browse file"
                    className="mt-[8px]"
                    disabled={props.isDisabled}
                    onClick={e => {
                        e.stopPropagation()
                        onManualSelect(e)
                    }}
                />
            </div>
        </div>
    )
}

interface IEraUploadFileDropzoneComponentProps {
    className?: string
    isDisabled?: boolean
    acceptedFileFormat: OneVerifyEraUploadFileFormat
    onValidationDone: (data: IEraUploadReviewState) => void
}
